import { useState, useEffect } from "react";
import tw from "tailwind-styled-components";
import { MdClose } from "react-icons/md";
import { CircularProgress } from "@mui/material";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { ActionSection, ModalHeader, SideCancelBtn, SubmitBtn } from "../Styles/InputStyles";

const ReceiptSettingsModal = ({
    onClose,
    settingsData,
    editSettingsMutate,
    isLoading,
    property,
    propertyList,
    fetchSettingsLoading,
    isDataLoaded,
    propertyParam,
    fetchReceiptSettingsMutate,
    editSettingsLoading,
    selectedProperties,
    setSelectedProperties
}) => {
    const [settings, setSettings] = useState({
        description: "",
        stayRules: ""
    });

    const [originalDescription, setOriginalDescription] = useState(""); // Track original description
    const [originalStayRules, setOriginalStayRules] = useState("");
    const [filteredProperties, setFilteredProperties] = useState([]);
    const [showPropertyModal, setShowPropertyModal] = useState(false);
    const [showMultiSelectModal, setShowMultiSelectModal] = useState(false);
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [selectedPropertyId, setSelectedPropertyId] = useState(null);
    const [isPropertyChanging, setIsPropertyChanging] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const allProperties = propertyList || [];

    const defaultDescription = `<ul>
        <li>This is an acknowledgment receipt of the payment made by the tenant through whatsoever mode of payment for the corresponding services.</li>
        <li>If an online payment fails for any reason, this receipt will be considered null and void.</li>
        <li>Refunds will not be provided under any circumstances for this receipt.</li>
    </ul>`;
    const defaultStayRules = `<ul>
        <li>This is an acknowledgment receipt of the payment made by the tenant through whatsoever mode of payment for the corresponding services.</li>
        <li>If an online payment fails for any reason, this receipt will be considered null and void.</li>
        <li>Refunds will not be provided under any circumstances for this receipt.</li>
    </ul>`;
    // Quill modules configuration
    const quillModules = {
        toolbar: [
            ['bold', 'italic', 'underline'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        ],
    };

    const quillFormats = [
        // 'header',
        'bold', 'italic', 'underline',
        // 'bold', 'italic', 'underline', 'strike',
        'list', 'bullet',
        // 'link',
        // 'color', 'background',
        // 'blockquote', 'code-block'
    ];

    useEffect(() => {
        if (settingsData) {
            const description =
                settingsData.receiptTerms || defaultDescription;

            const stayRules =
                settingsData.stayRules || "";

            setSettings({
                description,
                stayRules,
            });

            setOriginalDescription(description);
            setOriginalStayRules(stayRules);
        } else {
            setSettings({
                description: defaultDescription,
                // stayRules: "defaultStayRules",
            });

            setOriginalDescription(defaultDescription);
            // setOriginalStayRules(defaultStayRules);
        }
    }, [settingsData]);

    const handleDescriptionChange = (value) => {
        setSettings((prev) => ({
            ...prev,
            description: value,
        }));
    };

    const handleStayRulesChange = (value) => {
        setSettings((prev) => ({
            ...prev,
            stayRules: value,
        }));
    };
    const isEditorEmpty = (html) => {
        const text = html
            ?.replace(/<(.|\n)*?>/g, "")
            ?.replace(/&nbsp;/g, " ")
            ?.trim();

        return !text;
    };

    const handleSubmit = () => {
        const isDescriptionChanged = settings.description !== originalDescription;
        const isStayRulesChanged = settings.stayRules !== originalStayRules;

        if (!isDescriptionChanged && !isStayRulesChanged) {
            onClose();
            return;
        }
        setIsSubmitting(true);

        const payload = {
            description: settings.description,
            // stayRules: settings.stayRules || null,
            stayRules: isEditorEmpty(settings.stayRules)
                ? null
                : settings.stayRules,
        };

        editSettingsMutate(payload, {
            onSettled: () => {
                setIsSubmitting(false);
                onClose();
            }
        });
    };

    const showLoading = fetchSettingsLoading || isPropertyChanging;

    return (
        <div className="bg-white w-full h-full flex flex-col">
            <ModalHeader>
                <HeaderTitle>Rules & Receipt Settings</HeaderTitle>
                <CloseButton onClick={onClose}>
                    <MdClose color="white" size={24} />
                </CloseButton>
            </ModalHeader>

            {showLoading ? (
                <div className="flex items-center justify-center h-full">
                    <CircularProgress />
                </div>
            ) : (
                <>
                    <ContentContainer>
                        <ContentWrapper>
                            <Section>

                                {/* Only Description Field with Rich Text Editor */}
                                <DescriptionCard>
                                    <DescriptionHeader>
                                        <DescriptionTitle>Receipt Description</DescriptionTitle>
                                        <DescriptionSubtitle>
                                            Customize the description that will appear on tenant receipts
                                        </DescriptionSubtitle>
                                    </DescriptionHeader>
                                    <StyledQuillWrapper>
                                        <ReactQuill
                                            theme="snow"
                                            value={settings.description}
                                            onChange={handleDescriptionChange}
                                            modules={quillModules}
                                            formats={quillFormats}
                                            placeholder="Enter receipt description here..."
                                            readOnly={isLoading || isPropertyChanging}
                                        />
                                    </StyledQuillWrapper>
                                </DescriptionCard>


                                <DescriptionCard>
                                    <DescriptionHeader>
                                        <DescriptionTitle>Stay Rules</DescriptionTitle>
                                        <DescriptionSubtitle>
                                            Standard rules and regulations for the tenant's duration of stay
                                        </DescriptionSubtitle>
                                    </DescriptionHeader>
                                    <StyledQuillWrapper>
                                        <ReactQuill
                                            theme="snow"
                                            value={settings.stayRules}
                                            onChange={handleStayRulesChange}
                                            modules={quillModules}
                                            formats={quillFormats}
                                            placeholder="Enter stay rules here..."
                                            readOnly={isLoading || isPropertyChanging}
                                        />
                                    </StyledQuillWrapper>
                                </DescriptionCard>

                                {/* Action Buttons */}
                                <ActionSection>
                                    <SideCancelBtn type="button" onClick={onClose}>
                                        Cancel
                                    </SideCancelBtn>
                                    <SubmitBtn
                                        onClick={handleSubmit}
                                        disabled={isSubmitting || isLoading || isPropertyChanging}
                                    >
                                        {isSubmitting ? "Submitting..." : "Submit"}
                                    </SubmitBtn>
                                </ActionSection>

                            </Section>
                        </ContentWrapper>

                    </ContentContainer>
                </>
            )}
        </div>
    );
};

// Styled Components
const ContentContainer = tw.div`flex-1 overflow-y-auto mb-16`;
const ContentWrapper = tw.div`p-6`;
const Section = tw.div`space-y-6`;
const HeaderTitle = tw.h1`text-white text-lg font-medium`;
const CloseButton = tw.button``;

// Description field styled components
const DescriptionCard = tw.div`
  rounded-xl border border-gray-200 p-4 bg-white
`;

const DescriptionHeader = tw.div`
  mb-3
`;

const DescriptionTitle = tw.h3`
  text-sm font-semibold text-gray-800 mb-1
`;

const DescriptionSubtitle = tw.p`
  text-xs text-gray-500
`;

const StyledQuillWrapper = tw.div`
  [&_.ql-toolbar]:rounded-t-lg
  [&_.ql-toolbar]:border-gray-200
  [&_.ql-container]:rounded-b-lg
  [&_.ql-container]:border-gray-200
  [&_.ql-editor]:min-h-[200px]
  [&_.ql-editor]:text-sm
  [&_.ql-editor.ql-blank]:before:text-gray-400
`;

export default ReceiptSettingsModal;
